/**
 * Communication template mapping — Angular `ProductEditComponent` communication tab parity.
 * UI matches product module communication-templates screenshots.
 */
import type { ForwardedRef } from 'react'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import {
  Loader2,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Search,
  Info,
  Monitor,
  Smartphone,
} from 'lucide-react'
import { showInfo, showSuccess } from '@/lib/utils/notifications'
import type {
  CommWorkflowItem,
  EmailTemplateListItem,
  ProductCommunicationTemplatesTabHandle,
  ProductCommunicationTemplatesTabProps,
} from '../types'
import {
  fetchCommunicationWorkflows,
  fetchWorkflowEmailTemplates,
  saveCommunicationTemplates,
} from '../services/productApi'
import { sameNumberSet } from '../utils/productRiskMatrixModel'
import { cn } from '@/lib/utils/helpers'
import { formatProductDateTimeLong } from '../utils/productFormatters'

function MappedBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200/80',
        className,
      )}
    >
      Mapped
    </span>
  )
}

export const ProductCommunicationTemplatesTab = forwardRef(
  function ProductCommunicationTemplatesTab(
    { productId, onError, onDirtyChange }: ProductCommunicationTemplatesTabProps,
    ref: ForwardedRef<ProductCommunicationTemplatesTabHandle>,
  ) {
    const [loadingWf, setLoadingWf] = useState(true)
    const [loadingTpl, setLoadingTpl] = useState(false)
    const [saving, setSaving] = useState(false)
    const [workflows, setWorkflows] = useState<CommWorkflowItem[]>([])
    const [search, setSearch] = useState('')
    const [selectedWf, setSelectedWf] = useState<CommWorkflowItem | null>(null)
    const [templates, setTemplates] = useState<EmailTemplateListItem[]>([])
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [originalIds, setOriginalIds] = useState<number[]>([])
    const [previewId, setPreviewId] = useState<number | null>(null)
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop')
    const dropdownRef = useRef<HTMLDivElement>(null)
    const dirtyRef = useRef(false)
    const [dirty, setDirty] = useState(false)
    dirtyRef.current = dirty

    const setDirtyBoth = useCallback(
      (d: boolean) => {
        dirtyRef.current = d
        setDirty(d)
        onDirtyChange?.(d)
      },
      [onDirtyChange],
    )

    const selectWorkflow = useCallback(
      (wf: CommWorkflowItem) => {
        setSelectedWf(wf)
        setDropdownOpen(false)
        const mapped = (wf.mapped_template_ids || []).map((x) => +x).filter(Boolean)
        const sel = mapped.length ? [mapped[0]] : []
        setSelectedIds(sel)
        setOriginalIds([...sel])
        setPreviewId(sel.length ? sel[0] : null)
        setDirtyBoth(false)
      },
      [setDirtyBoth],
    )

    const selectedWfIdRef = useRef<number | null>(null)

    const loadWorkflows = useCallback(
      async (preserveWorkflowId?: number) => {
        if (!productId) return
        setLoadingWf(true)
        try {
          const data = await fetchCommunicationWorkflows(productId)
          const list = data.workflows || []
          setWorkflows(list)
          const preferred = preserveWorkflowId ?? selectedWfIdRef.current ?? undefined
          let wf: CommWorkflowItem | null = null
          if (preferred) wf = list.find((w) => +w.workflow_id === +preferred) || null
          if (!wf && list.length) {
            wf = list.find((w) => (w.mapped_count || 0) > 0) || list[0]
          }
          if (wf) {
            selectedWfIdRef.current = +wf.workflow_id
            selectWorkflow(wf)
          } else {
            selectedWfIdRef.current = null
            setSelectedWf(null)
            setTemplates([])
            setSelectedIds([])
            setOriginalIds([])
            setPreviewId(null)
            setDirtyBoth(false)
          }
        } catch (e) {
          onError((e as { message?: string }).message || 'Failed to load workflows')
        } finally {
          setLoadingWf(false)
        }
      },
      [productId, onError, selectWorkflow, setDirtyBoth],
    )

    useEffect(() => {
      selectedWfIdRef.current = null
      void loadWorkflows()
    }, [productId, loadWorkflows])

    const loadTemplates = useCallback(
      async (workflowId: number) => {
        if (!workflowId) return
        setLoadingTpl(true)
        try {
          const list = await fetchWorkflowEmailTemplates(workflowId, productId)
          setTemplates(list.map((t) => ({ ...t })))
          setSelectedIds((prev) => {
            const first = prev.length ? prev[0] : null
            setPreviewId(first)
            return prev
          })
        } catch (e) {
          onError((e as { message?: string }).message || 'Failed to load templates')
        } finally {
          setLoadingTpl(false)
        }
      },
      [productId, onError],
    )

    useEffect(() => {
      if (selectedWf?.workflow_id) void loadTemplates(selectedWf.workflow_id)
    }, [selectedWf?.workflow_id, loadTemplates])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setDropdownOpen(false)
        }
      }
      if (dropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside)
      }
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [dropdownOpen])

    const filteredWf = useMemo(() => {
      const q = search.toLowerCase().trim()
      if (!q) return workflows
      return workflows.filter(
        (w) =>
          (w.workflow_label || '').toLowerCase().includes(q) ||
          (w.workflow_type || '').toLowerCase().includes(q),
      )
    }, [workflows, search])

    const toggleTemplate = (id: number) => {
      const current = selectedIds.length ? selectedIds[0] : null
      const next = current === id ? [] : [id]
      setSelectedIds(next)
      setPreviewId(next.length ? next[0] : null)
      setDirtyBoth(!sameNumberSet(next, originalIds))
      setDropdownOpen(false)
    }

    const previewTemplate = previewId
      ? templates.find((t) => +t.template_id === +previewId && selectedIds.includes(+previewId))
      : null

    const selectedTemplate = selectedIds.length
      ? templates.find((t) => +t.template_id === selectedIds[0])
      : null

    const selectedLabel = selectedTemplate?.template_name || 'Select template'
    const hasMappedSelection = selectedIds.length > 0

    const saveImpl = async (): Promise<boolean> => {
      if (!selectedWf) {
        onError('Please select a workflow.')
        return false
      }
      if (!dirtyRef.current) {
        showInfo('No changes to save.')
        return false
      }
      setSaving(true)
      try {
        const res = await saveCommunicationTemplates(productId, {
          workflow_id: +selectedWf.workflow_id,
          template_ids: selectedIds.map((x) => +x),
        })
        if (selectedWf) {
          setSelectedWf({
            ...selectedWf,
            mapped_template_ids: res.mapped_template_ids || [...selectedIds],
            mapped_count:
              res.mapped_count != null ? res.mapped_count : selectedIds.length,
            last_saved_at: res.last_saved_at || selectedWf.last_saved_at,
            last_saved_by: res.last_saved_by || selectedWf.last_saved_by,
          })
        }
        setOriginalIds([...selectedIds])
        setDirtyBoth(false)
        showSuccess('Communication templates saved successfully!')
        return true
      } catch (e) {
        onError((e as { message?: string }).message || 'Failed to save')
        return false
      } finally {
        setSaving(false)
      }
    }

    const discardImpl = () => {
      setSelectedIds([...originalIds])
      setPreviewId(originalIds.length ? originalIds[0] : null)
      setDropdownOpen(false)
      setDirtyBoth(false)
    }

    useImperativeHandle(ref, () => ({
      save: saveImpl,
      discard: discardImpl,
      isDirty: () => dirtyRef.current,
    }))

    return (
      <div className="flex flex-col gap-4" data-testid="product-comm-templates-section">
        <div>
          <h2 className="m-0 text-lg font-bold text-slate-900">Communication templates</h2>
          <p className="mt-1 mb-0 text-sm text-slate-500">
            Select one workflow and map an email template to it.
          </p>
        </div>

        <div
          className="grid min-w-0 gap-4 lg:grid-cols-12"
          data-testid="product-comm-templates-tab"
        >
          {/* Left: workflows */}
          <div className="flex min-w-0 flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:col-span-4">
            <div className="mb-1 flex items-center gap-2">
              <h3 className="m-0 text-sm font-semibold text-slate-900">Select workflow</h3>
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-slate-100 px-1.5 text-[11px] font-semibold text-slate-600">
                {workflows.length}
              </span>
            </div>
            <p className="mb-3 mt-0 text-xs text-slate-500">
              Only one workflow can be mapped to this product.
            </p>

            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20"
                placeholder="Search workflows"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {loadingWf ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <div
                className="max-h-[32vh] min-h-[100px] space-y-1 overflow-y-auto overscroll-y-contain sm:max-h-[520px]"
                role="list"
                aria-label="Workflows"
              >
                {filteredWf.map((wf) => {
                  const wfId = +wf.workflow_id
                  const isSelected = selectedWf?.workflow_id === wf.workflow_id
                  const mappedCount =
                    (isSelected ? selectedIds.length : wf.mapped_count) || 0
                  const isMapped = mappedCount > 0
                  return (
                    <button
                      key={wf.workflow_id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => selectWorkflow(wf)}
                      data-testid={`product-comm-wf-radio-${wfId}`}
                      className={cn(
                        'flex w-full cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                        isSelected
                          ? 'border-[rgb(21_151_255)] bg-[rgb(21_151_255)]/5'
                          : 'border-transparent hover:bg-slate-50',
                      )}
                    >
                      <span className="min-w-0 flex-1 font-medium text-slate-800">
                        {wf.workflow_label}
                      </span>
                      {isMapped ? <MappedBadge /> : null}
                    </button>
                  )
                })}
                {!filteredWf.length && (
                  <p className="px-1 py-4 text-sm text-slate-500">No workflows match your search.</p>
                )}
              </div>
            )}
          </div>

          {/* Right: template + preview */}
          <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 lg:col-span-8">
            {!selectedWf && !loadingWf && (
              <p className="text-sm text-slate-500">No workflows found.</p>
            )}
            {selectedWf && (
              <>
                <div className="mb-4 flex flex-col gap-2 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <h3 className="m-0 min-w-0 break-words text-base font-semibold text-slate-900 sm:text-lg">
                      {selectedWf.workflow_label}
                    </h3>
                    {selectedWf.last_saved_at && (
                      <p className="mb-0 mt-1 text-xs text-slate-500">
                        Last updated: {formatProductDateTimeLong(selectedWf.last_saved_at)}
                        {selectedWf.last_saved_by ? ` by ${selectedWf.last_saved_by}` : ''}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={loadingWf || saving}
                    title="Reload"
                    aria-label="Reload"
                    onClick={() => void loadWorkflows(selectedWf.workflow_id)}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    <RefreshCw className={cn('h-4 w-4', loadingWf && 'animate-spin')} />
                  </button>
                </div>

                <div className="mb-1 text-sm font-medium text-slate-700">Email template</div>
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start">
                  <div className="relative min-w-0 flex-1" ref={dropdownRef}>
                    <button
                      type="button"
                      onClick={() => setDropdownOpen((o) => !o)}
                      className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left text-sm text-slate-800 shadow-sm transition hover:border-slate-300"
                    >
                      <span className="min-w-0 flex-1 truncate font-medium">{selectedLabel}</span>
                      {hasMappedSelection ? <MappedBadge /> : null}
                      {dropdownOpen ? (
                        <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                      )}
                    </button>
                    {dropdownOpen && (
                      <div className="absolute left-0 right-0 z-20 mt-1 max-h-56 w-full min-w-0 overflow-y-auto overscroll-y-contain rounded-lg border border-slate-200 bg-white shadow-lg">
                        {loadingTpl ? (
                          <div className="flex justify-center py-6">
                            <Loader2 className="h-5 w-5 animate-spin" />
                          </div>
                        ) : templates.length === 0 ? (
                          <p className="px-3 py-4 text-sm text-slate-500">No templates available.</p>
                        ) : (
                          templates.map((t) => {
                            const checked = selectedIds.includes(+t.template_id)
                            return (
                              <button
                                key={t.template_id}
                                type="button"
                                onClick={() => toggleTemplate(+t.template_id)}
                                className={cn(
                                  'flex w-full cursor-pointer items-center gap-2 border-b border-slate-50 px-3 py-2.5 text-left text-sm last:border-b-0 hover:bg-slate-50',
                                  checked && 'bg-[rgb(21_151_255)]/5',
                                )}
                              >
                                <span
                                  className={cn(
                                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                                    checked
                                      ? 'border-[rgb(21_151_255)] bg-[rgb(21_151_255)]'
                                      : 'border-slate-300 bg-white',
                                  )}
                                  aria-hidden
                                >
                                  {checked ? (
                                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                  ) : null}
                                </span>
                                <span className="min-w-0 flex-1 truncate">{t.template_name}</span>
                                {checked ? <MappedBadge /> : null}
                              </button>
                            )
                          })
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-start gap-2 rounded-lg border border-sky-100 bg-sky-50/80 px-3 py-2.5 text-xs text-sky-900 lg:max-w-[14rem] lg:shrink-0">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
                    <span>Changes are applied when you save the product.</span>
                  </div>
                </div>

                {dirty && (
                  <p className="mb-3 mt-0 text-xs font-medium text-amber-700">Unsaved changes</p>
                )}

                <div className="border-t border-slate-100 pt-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="m-0 text-base font-bold text-slate-900">Preview</h4>
                    <div
                      className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5"
                      role="group"
                      aria-label="Preview viewport"
                    >
                      <button
                        type="button"
                        onClick={() => setPreviewMode('desktop')}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition',
                          previewMode === 'desktop'
                            ? 'bg-[rgb(21_151_255)] text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-700',
                        )}
                      >
                        <Monitor className="h-3.5 w-3.5" />
                        Desktop
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewMode('mobile')}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition',
                          previewMode === 'mobile'
                            ? 'bg-[rgb(21_151_255)] text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-700',
                        )}
                      >
                        <Smartphone className="h-3.5 w-3.5" />
                        Mobile
                      </button>
                    </div>
                  </div>

                  {!previewTemplate ? (
                    <p className="text-sm text-slate-500">Select an email template to preview.</p>
                  ) : (
                    <div className="space-y-3">
                      <div className="divide-y divide-slate-300 rounded-lg border border-slate-300">
                        <div className="flex items-baseline gap-4 px-4 py-3">
                          <span className="w-24 shrink-0 text-sm font-bold text-slate-700">
                            To:
                          </span>
                          <span className="min-w-0 flex-1 break-all text-sm font-normal text-slate-900">
                            {previewTemplate.email_to || '—'}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-4 px-4 py-3">
                          <span className="w-24 shrink-0 text-sm font-bold text-slate-700">
                            Cc:
                          </span>
                          <span className="min-w-0 flex-1 break-all text-sm font-normal text-slate-900">
                            {previewTemplate.email_cc || '—'}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-4 px-4 py-3">
                          <span className="w-24 shrink-0 text-sm font-bold text-slate-700">
                            Reply-to:
                          </span>
                          <span className="min-w-0 flex-1 break-all text-sm font-normal text-slate-900">
                            {previewTemplate.reply_to || '—'}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-4 px-4 py-3">
                          <span className="w-24 shrink-0 text-sm font-bold text-slate-700">
                            Subject:
                          </span>
                          <span className="min-w-0 flex-1 text-sm font-normal text-slate-900">
                            {previewTemplate.email_subject || '—'}
                          </span>
                        </div>
                      </div>

                      <div
                        className={cn(
                          'mx-auto overflow-hidden rounded-lg border border-slate-200 bg-white transition-all',
                          previewMode === 'mobile' ? 'max-w-[375px]' : 'w-full',
                        )}
                      >
                        <iframe
                          title="email-preview"
                          className={cn(
                            'w-full bg-white',
                            previewMode === 'mobile' ? 'h-[420px]' : 'h-[220px] sm:h-[360px]',
                          )}
                          srcDoc={
                            previewTemplate.email_body ||
                            '<!DOCTYPE html><html><body></body></html>'
                          }
                          sandbox="allow-same-origin"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )
  },
)

export type { ProductCommunicationTemplatesTabHandle } from '../types/components'
